import {
  Review360Participants,
  Review360ParticipantStatus,
} from 'src/api/review360';
import { OthRegistrationSubmission } from 'src/api/othRegistration';

/** The number of responses a 360 round unlocks at when the API does not say. */
export const DEFAULT_UNLOCK_AT = 5;

/* Member copy (Norwegian bokmål, no em-dashes) */

export const collectingText = (
  respondents: number,
  unlockAt: number | undefined
): string => {
  const needed = unlockAt ?? DEFAULT_UNLOCK_AT;
  return `Samler svar: ${respondents} av ${needed}. Resultatene låses opp ved ${needed} svar.`;
};

export const unlockedText = (respondents: number): string =>
  respondents === 1
    ? '1 person har svart.'
    : `${respondents} personer har svart.`;

export const notFoundText = (email: string | null | undefined): string =>
  email
    ? `Vi fant ingen 360 knyttet til ${email}. Har du fått 360-lenker på en annen e-postadresse, si fra til programansvarlig, så kobles den hit. Er du ny, blir du lagt til, og da vises din 360 her.`
    : 'Vi fant ingen 360 knyttet til kontoen din. Programansvarlig legger deg til, og da vises din 360 her.';

export const UNAVAILABLE_TEXT = 'Kunne ikke hente 360 akkurat nå.';

export const LOADING_TEXT = 'Henter din 360 ...';

/** The origin a dashboard link points at, for the frame's message check. */
export const dashboardOrigin = (dashboardUrl: string): string | null => {
  try {
    return new URL(dashboardUrl).origin;
  } catch {
    return null;
  }
};

/* Leader copy (English, as the rest of the Participants tab) */

export const ADMIN_LOADING_TEXT = 'Opening 360review admin ...';

export const ADMIN_UNAVAILABLE_TEXT =
  'Could not open 360review admin right now.';

/** A leader who is not the account owner: who opens it, and where the status is. */
export const adminNotOwnerText = (
  accountOwnerEmail: string | null | undefined
): string =>
  accountOwnerEmail
    ? `The 360review admin is opened by the account owner (${accountOwnerEmail}). You can see every participant's 360 status in Participants.`
    : "The 360review admin is opened by the account owner. You can see every participant's 360 status in Participants.";

export const participantRoundText = (
  status: Review360ParticipantStatus | undefined
): string => {
  if (!status || !status.found) {
    return 'None';
  }
  const round = status.round;
  if (!round) {
    return 'Found';
  }
  switch (round.status) {
    case 'unlocked':
      return 'Unlocked';
    case 'closed':
      return 'Closed';
    default:
      return `Round ${round.ordinal} · ${round.respondents}/${round.unlockAt ?? DEFAULT_UNLOCK_AT}`;
  }
};

export const seatsText = (seats: Review360Participants['seats']): string =>
  seats
    ? `360review seats: ${seats.remaining} of ${seats.purchased} left`
    : '360review seats: unknown';

/**
 * Emails of participants who have no 360 yet, one per participant, in the
 * order of the submissions. Participants without any email are skipped (the
 * leader cannot add them anyway).
 */
export const emailsWithout360 = (
  submissions: Pick<OthRegistrationSubmission, 'user_id' | 'email'>[],
  results: Review360Participants['results']
): string[] => {
  const seen = new Set<string>();
  const emails: string[] = [];
  submissions.forEach((submission) => {
    const status = results[submission.user_id];
    if (status?.found) {
      return;
    }
    const email = (status?.email ?? submission.email ?? '')
      .trim()
      .toLowerCase();
    if (!email || seen.has(email)) {
      return;
    }
    seen.add(email);
    emails.push(email);
  });
  return emails;
};
